import { matchPath } from "react-router-dom";
import { AppPermissionItem } from "../dto";
import { CSSProperties } from "react";

export * from "@gw/gw-utils";
/**
 * 排序tree
 * @param tree 树形结构数据
 * @param orderby 排序字段
 */
export function sortTree(tree: any[], orderby: string) {
  if (!tree?.length) return [];
  const stack = tree.slice();

  function getSortArr(currArr: any[]) {
    currArr.sort((a, b) => a[orderby] - b[orderby]);
    currArr.forEach((item) => {
      if (item.children) {
        getSortArr(item.children);
      }
    });
  }
  getSortArr(stack);
  return stack;
}
/**
 * 去除树中指定的key
 * @param tree
 * @param keys 不需要的key
 */
export function cleanTree(tree: any[]) {
  if (!tree?.length) return [];
  const stack = tree.slice();

  function getCleanTree(
    currArr: any[],
    keys = ["parentId", "parentRef", "isLeaf", "orderIdx"]
  ) {
    currArr.forEach((m) => {
      for (const key in m) {
        if (m.children?.length) {
          getCleanTree(m.children);
        }
        if (keys.includes(key)) delete m[key];
      }
    });
  }
  getCleanTree(stack);
  return stack;
}

/**
 * 根据url 获取顶层节点
 * @param pathname 当前hash-route
 * @param authMenuList 扁平菜单列表
 * @returns
 */
export function getTopNode(pathname: string, menuList: AppPermissionItem[]) {
  let componentId = "";

  let item = menuList
    .filter((m) => m.componentType === 0)
    .find((m) => matchPath(pathname, m.componentUrl)?.isExact);

  function getComponentId() {
    if (!item?.parentId) {
      componentId = item?.componentId;
      return;
    }
    item = menuList.find((m) => m.id === item.parentId);
    getComponentId();
  }
  getComponentId();

  return componentId;
}

/**
 * 去除树中为空的非叶子节点
 * @param tree
 * @param 叶子节点的判定方法
 * @returns
 */
export function removeEmptyNodes(tree: any[], isLeaf: (item: any) => boolean) {
  // 如果树不是数组，直接返回
  if (!Array.isArray(tree)) {
    return tree;
  }

  return tree
    .map((node) => {
      // 递归处理每个节点
      if (node && typeof node === "object") {
        if (!isLeaf(node) && !node?.children) {
          return null; // 返回 null 表示删除该节点
        }

        if (Array.isArray(node.children)) {
          node.children = removeEmptyNodes(node.children, isLeaf).filter(
            Boolean
          ); // 过滤掉为 null 的子节点
        } else if (node.children && typeof node.children === "object") {
          [node.children] = removeEmptyNodes([node.children], isLeaf).filter(
            Boolean
          ); // 处理单个子节点
        }
      }
      return node;
    })
    .filter(Boolean); // 过滤掉为 null 的节点
}

/**
 * 生成一个样式对象
 * @param name 样式名
 * @param value 样式值
 * @param defaultValue 默认样式值
 * @returns {null | CSSProperties}
 */
export const combineStyle = (
  name: keyof CSSProperties,
  value: string | number | undefined,
  defaultValue?: string | number | undefined
) => {
  if (value || defaultValue) {
    return { [name]: value || defaultValue } as CSSProperties;
  }
  return null;
};

/**
 * 连接多个对象
 * @param args 单个对象
 */
export const combineObject = <T extends Record<any, any>>(args: T[]) => {
  return args.reduce((prev, curr) => {
    return { ...prev, ...curr };
  }, {});
};
/** 远程模块约定标记 */
export const REMOTE_TAG = "@";

/**
 * 获取远程模块的url
 * @param url 特殊格式的url
 * @returns string
 */
export const getRemoteModuleUrl = (componentUrl: string): string => {
  if (!componentUrl.includes(REMOTE_TAG)) return componentUrl;

  let compUrl = componentUrl.replace(REMOTE_TAG, "-");
  // 如果不是完整地址并且没有以/开头，加上/
  if (!compUrl.includes("://") && !compUrl.startsWith("/")) {
    compUrl = `/${compUrl}`;
  }
  return compUrl;
};

/**
 * 打开菜单
 * @param url 菜单url
 * @param cb 回调函数
 */
export function openComponentUrl(url?: string, cb?: (s: string) => void) {
  if (!url) {
    return;
  }
  let targetUrl = url;
  /** 兼容url带参数的情况 */
  if (targetUrl.includes(":")) {
    const paramIndex = targetUrl.indexOf(":") - 1;
    targetUrl = targetUrl.substring(0, paramIndex);
  }
  /** 远程组件跳转处理 */
  if (url.includes(REMOTE_TAG)) {
    const [container, comp] = url.split(REMOTE_TAG);
    targetUrl = `/${container}-${comp}`;
    /** 兼容url带参数的情况 */
    if (targetUrl.includes(":")) {
      [targetUrl] = targetUrl.split(":");
    }
  }
  /** 模块跳转 */
  cb?.(targetUrl);
}

/**
 * 解析单个菜单项
 */
export function resolveMenuItem(d: AppPermissionItem) {
  if (d.componentUrl.includes(REMOTE_TAG)) {
    const [container, module] = d.componentUrl.split(REMOTE_TAG);

    const compUrl = getRemoteModuleUrl(d.componentUrl);

    return {
      ...d,
      componentUrl: compUrl, // 转成普通的地址
      container,
      module: module?.split("/")[0],
    };
  }
  return d;
}

export { default as useRequest, apis } from "./use-request";
